<script>
import CandidateImage from '@/components/candidate/candidate-image.vue'
import candidateMixin from '@/mixins/candidateMixin'
import BookingCardCandidate from '@components/booking/booking-card-candidate.vue'
import { debounce } from 'lodash'

const sortBy = Object.freeze({
  Name: 'name',
  Favourite: 'favourite',
  Recent: 'recent',
})

export default {
  name: 'CandidateList',
  components: {
    CandidateImage,
    BookingCardCandidate,
  },
  mixins: [candidateMixin],
  props: {
    candidateData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      candidateListSort: [],
      searchTerm: '',
      options: [
        {
          id: 'list-view-name',
          icon: 'mdi-alphabetical',
          value: sortBy.Name,
          tooltip: this.$t('candidateList.sortOptions.name'),
          emit: 'list',
        },
        {
          id: 'list-view-favourite',
          icon: 'mdi-star',
          value: sortBy.Favourite,
          tooltip: this.$t('candidateList.sortOptions.favourite'),
          emit: 'list',
        },
        {
          id: 'list-view-recent',
          icon: 'mdi-calendar',
          value: sortBy.Recent,
          tooltip: this.$t('candidateList.sortOptions.recent'),
          emit: 'tile',
        },
      ],
      btnToggle: sortBy.Favourite,
    }
  },
  computed: {
    compBtnToggle: {
      // getter
      get: function() {
        return this.btnToggle
      },
      // setter
      set: function(newValue) {
        this.btnToggle = newValue
        this.filterCandidateList()
      },
    },
  },
  watch: {
    searchTerm(val) {
      this.searchTerm = val
      this.debouncedFilterCandidateList()
    },
    candidateData(candidates) {
      if (this.searchTerm === '' || this.searchTerm === null) {
        this.sortList(candidates, this.btnToggle)
      } else {
        this.filterCandidateList()
      }
    },
  },
  created() {
    // set up debounce for candidate search filter
    this.debouncedFilterCandidateList = debounce(this.filterCandidateList, 200)
  },
  async mounted() {
    this.candidateListSort = []
    this.sortList(this.candidateData, this.btnToggle)
  },
  methods: {
    sortList(data, sortMode) {
      const { Name, Favourite, Recent } = sortBy

      switch (sortMode) {
        case Name:
          this.candidateListSort = this.sortByName(data)
          break
        case Favourite:
          this.candidateListSort = this.sortByFavourite(data)
          break
        case Recent:
          this.candidateListSort = this.sortByRecent(data)
          break
      }
    },
    sortByName(data) {
      return data.slice().sort((candidateA, candidateB) => {
        return candidateA.firstName.localeCompare(candidateB.firstName)
      })
    },
    sortByFavourite(data) {
      return data.slice().sort((candidateA, candidateB) => {
        const aHasFav = candidateA.preferredLevels.length > 0
        const bHasFav = candidateB.preferredLevels.length > 0

        if (aHasFav !== bHasFav) {
          return bHasFav ? 1 : -1
        }
        return candidateA.firstName.localeCompare(candidateB.firstName)
      })
    },
    sortByRecent(data) {
      return data.slice().sort((candidateA, candidateB) => {
        const aDate = candidateA.mostRecentBookingDateLocal
        const bDate = candidateB.mostRecentBookingDateLocal
        if (aDate && bDate) return new Date(bDate) - new Date(aDate)
        if (aDate) return -1
        if (bDate) return 1
        return candidateA.firstName.localeCompare(candidateB.firstName)
      })
    },
    filterCandidateList() {
      let dataToSort = this.candidateData

      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const searchTerm = this.searchTerm.toLowerCase()
        dataToSort = this.candidateData.filter((candidate) => {
          const fullName =
            candidate.firstName.toLowerCase() +
            ' ' +
            candidate.lastName.toLowerCase()
          return fullName.includes(searchTerm)
        })
      }

      this.sortList(dataToSort, this.btnToggle)
    },
    viewCandidate(candidate) {
      this.$emit('viewCandidate', candidate.candidateId)
    },
  },
}
</script>

<template>
  <div>
    <v-toolbar flat class="transparent mb-8 mt-6 px-0 mx-0">
      <v-text-field
        id="candidateSearch"
        v-model="searchTerm"
        :label="`${$t('input.searchLabel')}`"
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
      >
      </v-text-field>

      <v-spacer></v-spacer>

      <v-btn-toggle v-model="compBtnToggle" group color="primary" mandatory>
        <v-tooltip v-for="option of options" :key="option.id" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :id="option.id"
              class="rounded"
              :value="option.value"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon> {{ option.icon }} </v-icon>
            </v-btn>
          </template>
          <span v-text="option.tooltip"></span>
        </v-tooltip>
      </v-btn-toggle>
    </v-toolbar>

    <v-row>
      <v-col
        v-for="candidate in candidateListSort"
        :key="`candidate_preferred_${candidate.candidateId}`"
        cols="12"
        md="4"
      >
        <v-card
          class="mb-5 d-flex custom-elevation rounded-xl"
          height="100%"
          elevation="0"
        >
          <v-card-text class="candidateItem">
            <div style="display:flex;">
              <div style="flex-grow:0">
                <v-lazy>
                  <CandidateImage
                    :id="candidate.candidateId"
                    :first-name="candidate.firstName"
                    :last-name="candidate.lastName"
                    :show-preferred-icon="candidate.preferredLevels.length > 0"
                    size="90"
                    :font-size="22"
                    rounded
                    clickable
                    @profileImgSrc="candidate.profileImgSrc = $event"
                    @click="viewCandidate(candidate)"
                  />
                </v-lazy>
              </div>
              <div style="flex-basis:0;flex-grow:1;">
                <BookingCardCandidate
                  :id="candidate.candidateId"
                  :first-name="candidate.firstName"
                  :last-name="candidate.lastName"
                  @click="viewCandidate(candidate)"
                />
                <div>
                  <span class="font-weight-medium">
                    {{
                      $t('candidateList.candidateListBookingCountTitle', {
                        count: candidate.numberOfBookings,
                      })
                    }}
                  </span>
                </div>
                <div
                  ><span class="font-weight-medium">
                    {{
                      $t('candidateList.candidateListLastBookingDateTitle', {
                        date: displayDate(candidate.mostRecentBookingDateLocal),
                      })
                    }}
                  </span>
                </div>
                <div>
                  <div
                    v-for="(preferredLevel, index) in candidate.preferredLevels"
                    :key="
                      `candidate_preferred_preferredLevel_${candidate.candidateId}` +
                        index
                    "
                  >
                    <div
                      v-if="preferredLevel.preferredLevelSetAt === 'LOCATION'"
                      ><v-icon dense>mdi-pin</v-icon>
                      <span class="font-weight-medium">
                        {{
                          $t('candidateList.candidateListLocationLinksTitle', {
                            links: preferredLevel.linkCount,
                          })
                        }}
                      </span>
                    </div>
                    <div
                      v-else-if="
                        preferredLevel.preferredLevelSetAt === 'CLIENT'
                      "
                    >
                      <v-icon dense>mdi-home</v-icon>
                      <span class="font-weight-medium">
                        {{
                          $t('candidateList.candidateListClientLinks', {
                            links: preferredLevel.linkCount,
                          })
                        }}
                      </span>
                    </div>
                    <div
                      v-else-if="
                        preferredLevel.preferredLevelSetAt === 'CLIENT_GROUP'
                      "
                    >
                      <v-icon dense>mdi-bank</v-icon>
                      <span class="font-weight-medium">
                        {{
                          $t('candidateList.candidateListClientGroupLinks', {
                            links: preferredLevel.linkCount,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
::v-deep.v-toolbar .v-toolbar__content {
  padding-right: 0 !important;
  padding-left: 0 !important;
}
</style>
